import React from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { copyToClipboard } from '@/utils/clipboard';

interface ApprovalOutletContext {
  clientId: string;
  approvalToken?: string;
}

interface ErrorDetails {
  type: string;
  message: string;
  details?: string;
  timestamp: string;
  rawError?: string;
}

export const SocialMediaErrorRoute: React.FC = () => {
  const { clientId } = useOutletContext<ApprovalOutletContext>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorType = searchParams.get('type') || 'unknown';
  const errorMessage = searchParams.get('message') || '';
  const errorDetails = searchParams.get('details') || '';
  const rawError = searchParams.get('raw') || ''; // Raw error from backend

  const getErrorInfo = (): ErrorDetails => {
    const errorMap: Record<string, { message: string; details?: string }> = {
      oauth_failed: {
        message: 'Autorizzazione Facebook Fallita',
        details: 'L\'autorizzazione con Facebook non è andata a buon fine. Questo può accadere se hai negato i permessi o se c\'è stato un problema con l\'app Facebook.',
      },
      invalid_state: {
        message: 'Errore di Sicurezza',
        details: 'Il token di sicurezza non è valido. Questo può accadere se la sessione è scaduta o se il link è stato modificato.',
      },
      no_pages: {
        message: 'Nessuna Pagina Facebook Trovata',
        details: 'Il tuo account Facebook non ha pagine da gestire. Per utilizzare questa funzione, devi creare una pagina Facebook prima di continuare.',
      },
      api_error: {
        message: 'Errore API Facebook',
        details: 'Si è verificato un errore durante la comunicazione con Facebook. Riprova tra qualche minuto.',
      },
      callback_failed: {
        message: 'Errore del Callback',
        details: 'Si è verificato un errore imprevisto durante il processo di autorizzazione.',
      },
      missing_state: {
        message: 'Parametro State Mancante',
        details: 'Il parametro di stato OAuth è mancante. Questo può accadere se l\'app Facebook è in modalità sviluppo e il tuo account non è autorizzato come tester.',
      },
      development_mode: {
        message: 'App Facebook in Modalità Sviluppo',
        details: 'L\'app Facebook è attualmente in modalità sviluppo. Solo gli utenti autorizzati possono connettersi. Contatta l\'amministratore per essere aggiunto come tester.',
      },
      unknown: {
        message: 'Errore Sconosciuto',
        details: 'Si è verificato un errore imprevisto. Contatta il supporto con uno screenshot di questa pagina.',
      },
    };

    const info = errorMap[errorType] || errorMap.unknown;

    return {
      type: errorType,
      message: errorMessage || info.message,
      details: errorDetails || info.details || '',
      timestamp: new Date().toISOString(),
      rawError: rawError || undefined,
    };
  };

  const errorInfo = getErrorInfo();

  const handleBackToDashboard = () => {
    navigate(`/client/${clientId}`);
  };

  const handleRetry = () => {
    navigate(`/client/${clientId}/social-media`);
  };

  const handleCopyError = () => {
    const errorText = `
Errore Connessione Social Media
================================

INFORMAZIONI UTENTE:
Tipo: ${errorInfo.type}
Messaggio: ${errorInfo.message}
Dettagli: ${errorInfo.details}

INFORMAZIONI TECNICHE:
Timestamp: ${errorInfo.timestamp}
Client ID: ${clientId}
URL: ${window.location.href}

ERRORE RAW (Backend):
${errorInfo.rawError || 'Nessun errore raw disponibile'}

QUERY PARAMS:
${Array.from(searchParams.entries()).map(([key, value]) => `${key}: ${value}`).join('\n')}
    `.trim();

    copyToClipboard(errorText);
  };

  const getTroubleshootingSteps = (): string[] => {
    const stepsMap: Record<string, string[]> = {
      oauth_failed: [
        'Assicurati di aver accettato tutti i permessi richiesti',
        'Verifica di aver effettuato l\'accesso con l\'account Facebook corretto',
        'Prova a disconnetterti da Facebook e riprovare',
      ],
      invalid_state: [
        'Ricarica la pagina e riprova',
        'Assicurati di non aver modificato il link',
        'Prova a disconnetterti e riconnetterti',
      ],
      no_pages: [
        'Vai su facebook.com/pages/create e crea una pagina Facebook',
        'Assicurati di essere l\'amministratore della pagina',
        'Riprova dopo aver creato la pagina',
      ],
      api_error: [
        'Attendi qualche minuto e riprova',
        'Verifica la tua connessione internet',
        'Se il problema persiste, contatta il supporto',
      ],
      missing_state: [
        'Contatta l\'amministratore per verificare lo stato dell\'app Facebook',
        'Assicurati di essere autorizzato come tester nell\'app Facebook',
        'Verifica che l\'app sia configurata correttamente',
      ],
      development_mode: [
        'Contatta l\'amministratore per essere aggiunto come tester',
        'Fornisci il tuo indirizzo email Facebook all\'amministratore',
        'Attendi la conferma prima di riprovare',
      ],
      unknown: [
        'Fai uno screenshot di questa pagina',
        'Invia lo screenshot all\'amministratore',
        'Prova a disconnetterti e riconnetterti',
      ],
    };

    return stepsMap[errorInfo.type] || stepsMap.unknown;
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Error Icon and Title */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Connessione Social Media Fallita
          </h1>
          <p className="text-lg text-gray-600 mb-6">{errorInfo.message}</p>

          {/* Error Details */}
          {errorInfo.details && (
            <div className="w-full bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-700">{errorInfo.details}</p>
            </div>
          )}

          {/* Error Metadata */}
          <div className="w-full bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-500">Tipo Errore:</span>
                <p className="font-mono text-gray-900">{errorInfo.type}</p>
              </div>
              <div>
                <span className="text-gray-500">Data/Ora:</span>
                <p className="font-mono text-gray-900">
                  {new Date(errorInfo.timestamp).toLocaleString('it-IT')}
                </p>
              </div>
            </div>
            {errorInfo.rawError && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <span className="text-gray-500 text-xs">Errore Tecnico:</span>
                <p className="font-mono text-xs text-gray-900 mt-1 break-all">
                  {errorInfo.rawError}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <button
              onClick={handleRetry}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Riprova
            </button>

            <button
              onClick={handleCopyError}
              className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copia Errore
            </button>
          </div>

          <button
            onClick={handleBackToDashboard}
            className="mt-3 w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>

      {/* Troubleshooting Steps */}
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <svg
            className="w-6 h-6 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          Cosa Puoi Fare
        </h2>

        <ol className="space-y-3">
          {getTroubleshootingSteps().map((step, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </span>
              <span className="text-gray-700 pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Contact Support */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex gap-3">
          <svg
            className="w-6 h-6 text-blue-600 shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">
              Hai bisogno di aiuto?
            </p>
            <p className="text-sm text-blue-700">
              Se il problema persiste dopo aver seguito questi passaggi, usa il pulsante "Copia Errore" sopra
              e invia i dettagli al tuo amministratore insieme a uno screenshot di questa pagina.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
